import { Children, type ReactElement, type ReactNode } from 'react';

import { Intent, IntentProps } from '../../../core/slots/Intent/Intent.js';
import { isSlot } from '../../../core/utils/is-slot.js';
import { ColorRule, isValidColorRule } from '../../core/slots/ColorRule.js';
import { ChartToolbar } from '../../core/slots/toolbar/ChartToolbar.js';
import { sanitizeProps } from '../../core/slots/toolbar/CompactToolbar.js';
import { DownloadData } from '../../core/slots/toolbar/DownloadData.js';
import { extractBehaviorTrackingProps } from '../../core/utils/extract-behavior-tracking-props.js';
import { DEFAULT_LEGEND_POSITION } from '../constants.js';
import { HoneycombChartDownloadCSVSlot } from '../slots/DownloadCSV.js';
import { HoneycombLegend } from '../slots/HoneycombLegend.js';
import { Toolbar } from '../slots/Toolbar.js';
import type { HoneycombSlotsConfig } from '../types/honeycomb-internal.js';

const buildChartToolbarConfigSlot = (
  child: ReactElement,
  config: HoneycombSlotsConfig,
) => {
  const { children, ...toolbar } = child.props;
  const childrenToolbar = iterateChartToolbarSlots(children);

  return {
    ...config,
    toolbar: {
      ...toolbar,
      ...childrenToolbar,
    },
  };
};

export const iterateHoneycombSlots = (children: ReactNode) => {
  let config: HoneycombSlotsConfig = {
    legend: undefined,
    toolbar: undefined,
    colorRules: [],
  };
  let intents: IntentProps[] | undefined;
  Children.forEach(children, (child) => {
    if (isSlot(child, HoneycombLegend)) {
      config = {
        ...config,
        legend: {
          position: DEFAULT_LEGEND_POSITION,
          ...child.props,
        },
      };
      // eslint-disable-next-line @typescript-eslint/no-deprecated
    } else if (isSlot(child, ChartToolbar)) {
      config = buildChartToolbarConfigSlot(child, config);
    } else if (isSlot(child, Toolbar)) {
      config.toolbar = {
        ...config.toolbar,
        ...sanitizeProps(child.props, ['draggable', 'hidden', 'placement']),
        ...extractBehaviorTrackingProps(child.props),
      };
    } else if (isSlot(child, HoneycombChartDownloadCSVSlot)) {
      config = {
        ...config,
        toolbar: { ...config.toolbar, downloadCSV: { enabled: true } },
      };
    } else if (isSlot(child, Intent)) {
      intents = intents ? [...intents, child.props] : [child.props];
    } else if (isSlot(child, ColorRule)) {
      isValidColorRule(child.props) && config.colorRules.push(child.props);
    }
  });

  if (intents) {
    config = {
      ...config,
      toolbar: {
        ...config.toolbar,
        intents,
        hidden: config.toolbar?.hidden ?? false,
      },
    };
  }

  return config;
};

/**
 * Generates chart toolbar config based on slots
 * @param children - ChartToolbar children
 * @returns ChartToolbar slot config
 */
const iterateChartToolbarSlots = (children: ReactNode) => {
  let toolbar = {};

  Children.forEach(children, (childToolbar) => {
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    if (isSlot(childToolbar, DownloadData)) {
      toolbar = {
        ...toolbar,
        downloadCSV: { enabled: true },
      };
    }
  });

  return toolbar;
};
